import './ProductInformation.scss';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthProvider';
import { formatPrice } from "../../utils/FormatPrice";
import BarcodeComponent from '../BarcodeComponent';
import { NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTriangleExclamation, faIdCard, faNotesMedical, faCircleInfo,
  faArrowUp, faArrowDown, faArrowTrendDown, faCircleCheck, faCircleXmark,
  faTag, faTableList, faCoins, faBan, faPen, faHospital, faBell, faBoxesStacked, faCalendarXmark,
  faChevronLeft, faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from 'react-tooltip';
import SectionCard from '../SectionCard/SectionCard';
import LaboratoryLogo from '../Laboratory/LaboratoryLogo';
import { transliterateToArabic } from '../../utils/transliterateArabic';

const TRUNCATE_AT = 220;

// Icon tiles of the price / stock band and the section titles: the brand blue (soft tint +
// blue icon) rather than the app's neutral gray — livelier, without adding new colors
// "Stock disponible" icon follows the stock level (the only colored tile of the band):
// green above 3 units, amber 1–3, red when out
const STOCK_ICON_STYLES = {
  ok:  { background: '#ecfdf5', color: '#059669' },
  low: { background: '#fffbeb', color: '#d97706' },
  out: { background: '#fef2f2', color: '#dc2626' },
};

// …and its glyph too, as the old badge next to the product name did: ↑ stocked, ↘ low, ↓ out
const STOCK_ICONS = { ok: faArrowUp, low: faArrowTrendDown, out: faArrowDown };

// "+5" / "-37" (minus as U+2212) for the price gap lines
const signedPct = (n) => (n > 0 ? `+${n}` : n < 0 ? `−${Math.abs(n)}` : '0');

// Next-expiration card turns red within this many days
const EXPIRY_SOON_DAYS = 90;

const BRAND_ICON_STYLE = { background: '#eff6ff', color: '#2563eb' };

// Amount in the value's style, currency after it small and gray (as money is shown elsewhere).
// formatPrice ends with " <currency>" — split on that last regular space (the thousands
// separator is a narrow no-break space, so it's never matched).
const PriceWithUnit = ({ amount }) => {
  const full = formatPrice(amount);
  const cut = full.lastIndexOf(' ');
  if (cut < 0) return full;
  return <>{full.slice(0, cut)} <span className="product-price-unit">{full.slice(cut + 1)}</span></>;
};

// strong: bold value, for the key facts a pharmacist scans for (dosage, active ingredients).
const FieldRow = ({ label, value, danger = false, children, truncate = false, strong = false }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const content = children ?? value ?? '—';
  const isLong = truncate && typeof content === 'string' && content.length > TRUNCATE_AT;

  return (
    <div className="product-field-row">
      {label && <span className="product-field-label">{label}</span>}
      <span
        className="product-field-value"
        style={strong ? { fontWeight: 600 } : undefined}
      >
        {isLong && !expanded ? (
          <>
            {content.slice(0, TRUNCATE_AT)}{'… '}
            <button className="product-see-more" onClick={() => setExpanded(true)}>{t('product.see_more')}</button>
          </>
        ) : (
          <>
            {content}
            {isLong && (
              <>{' '}<button className="product-see-more" onClick={() => setExpanded(false)}>{t('product.see_less')}</button></>
            )}
          </>
        )}
      </span>
    </div>
  );
};

const getStatusLabel = (status, t) => {
  if (status === 'MARKETED') return t('Marketed');
  if (status === 'NOT_MARKETED') return t('Not marketed');
  return status;
};

const Badge = ({ label, variant = 'info', icon }) => (
  <span className={`product-badge product-badge--${variant}`}>
    {icon && <FontAwesomeIcon icon={icon} />}
    {label}
  </span>
);

// Single-line, horizontally-scrollable badge strip — wrapping onto several rows ate
// too much vertical space on narrow screens, so this scrolls instead, with nav
// buttons that only appear once there's actually more to scroll to.
const ProductBadgeRow = ({ badges }) => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  };

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [badges.length]);

  const scrollBy = (dx) => scrollRef.current?.scrollBy({ left: dx, behavior: 'smooth' });

  return (
    <div className="product-badge-scroller">
      {canScrollLeft && (
        <button type="button" className="product-badge-nav product-badge-nav--left" onClick={() => scrollBy(-120)} aria-label="Défiler à gauche">
          <FontAwesomeIcon icon={faChevronLeft} />
        </button>
      )}
      <div className="product-badge-row" ref={scrollRef}>
        {badges.map((b, i) => (
          <Badge key={i} label={b.label} variant={b.variant} icon={b.icon} />
        ))}
      </div>
      {canScrollRight && (
        <button type="button" className="product-badge-nav product-badge-nav--right" onClick={() => scrollBy(120)} aria-label="Défiler à droite">
          <FontAwesomeIcon icon={faChevronRight} />
        </button>
      )}
    </div>
  );
};

const getBadges = (product, t) => {
  const badges = [];

  if (product.status) {
    const isActive = product.status.toUpperCase() === 'MARKETED';
    badges.push({
      label: getStatusLabel(product.status, t),
      variant: isActive ? 'success' : 'neutral',
      icon: isActive ? faCircleCheck : faCircleXmark,
    });
  }

  if (product.atcCode) {
    badges.push({ label: `ATC: ${product.atcCode}`, variant: 'neutral', icon: faTag });
  }

  if (product.tableLetter) {
    badges.push({ label: `${t('product.table')} ${product.tableLetter}`, variant: 'neutral', icon: faTableList });
  }

  if (product.psychoactiveSubstance && product.psychoactiveSubstance !== '--') {
    badges.push({ label: t('product.psychoactive_substance'), variant: 'neutral', icon: faTriangleExclamation });
  }

  if (product.refundable === true) {
    badges.push({ label: t('Refundable'), variant: 'info', icon: faCoins });
  } else if (product.refundable === false) {
    badges.push({ label: t('product.non_refundable'), variant: 'neutral', icon: faBan });
  }

  return badges;
};

// part: "header" (identity + price band), "fields" (the two field panels) or both (default) —
// the product sheet shows the header on top and the fields in its "Aperçu" tab
// onStockClick: when given, the available stock figure opens the sheet's Stock section
const ProductInformation = ({ product, part = "all", onEditBarcode, onEditPublicPrice, onEditHospitalPrice, onEditMinThreshold, onEditMaxThreshold, onStockClick }) => {
  const { t, i18n } = useTranslation();
  const { settings } = useAuth();
  const isArabic = i18n.language === 'ar';
  // Prefer the stored Arabic translation; until the catalog is translated, fall
  // back to an on-the-fly phonetic transliteration of the French text.
  const arText = (arValue, frValue) => (isArabic ? (arValue || transliterateToArabic(frValue)) : frValue);
  const displayName = arText(product.nameAr, product.name);
  const badges = getBadges(product, t);
  // Mirrors StockAlertService's effectiveThreshold on the backend: a product
  // only overrides the pharmacy-wide default once it has its own positive
  // threshold — 0/null/undefined all mean "use the global setting".
  const globalStockAlertThreshold = Number(settings?.stock_inventory_min_stock_alert) || 10;
  const effectiveStockAlertThreshold = product.minimumStock > 0 ? product.minimumStock : globalStockAlertThreshold;
  const stockQty = product.totalStock ?? 0;
  const stockLevel = stockQty > 3 ? 'ok' : stockQty > 0 ? 'low' : 'out';
  // Context lines under the band's values: hospital price vs public price, stock vs alert threshold
  const pphGapPct = product.publicPrice > 0 && product.hospitalPrice
    ? Math.round(((product.hospitalPrice - product.publicPrice) / product.publicPrice) * 100)
    : null;
  const stockVsThreshold = stockQty - effectiveStockAlertThreshold;
  // gauge: full at twice the alert threshold (or at the max stock when one is set)
  const stockGaugeMax = product.maximumStock > 0 ? product.maximumStock : Math.max(effectiveStockAlertThreshold * 2, 1);
  const stockGaugePct = Math.min(100, Math.round((stockQty / stockGaugeMax) * 100));
  // "YYYY-MM-DD" from the backend, read as a local date (no UTC shift)
  const nearestExpiration = product.nearestExpiration ? new Date(product.nearestExpiration + "T00:00:00") : null;
  const daysToExpiry = nearestExpiration
    ? Math.round((nearestExpiration - new Date(new Date().toDateString())) / 86400000)
    : null;
  const subPrefix = [arText(product.presentationAr, product.presentation), arText(product.dosageAr, product.dosage)].filter(Boolean).join(' · ');

  return (
    <div className="product-info-wrapper">

      {/* ── Header ── */}
      {part !== 'fields' && (
      <div className="product-header-card">
        <div className="product-header-body">
          <img
            className="product-avatar"
            src={product.images?.length > 0 ? product.images[0].url : '/images/no_product.png'}
            alt={product.name}
          />
          <div className="product-header-info">
            <p className="product-name" dir={isArabic ? 'rtl' : undefined}>
              {displayName}
            </p>
            <p className="product-sub">
              {subPrefix}
              {subPrefix && product.laboratory?.name && ' · '}
              {product.laboratory?.id ? (
                <NavLink to={`/suppliers/${product.laboratory.id}`} className="product-sub-lab-link">
                  {product.laboratory.name}
                </NavLink>
              ) : product.laboratory?.name}
            </p>
            {badges.length > 0 && <ProductBadgeRow badges={badges} />}
          </div>
          {product.barcode ? (
            <div className="product-header-barcode">
              <BarcodeComponent value={product.barcode} format="EAN13" height={36} fontSize={12} />
            </div>
          ) : (
            <div className="product-header-barcode product-header-barcode--empty">
              <span>{t('product.no_barcode')}</span>
              {onEditBarcode && (
                <button type="button" className="product-header-barcode__edit" onClick={onEditBarcode}>
                  <FontAwesomeIcon icon={faPen} />
                  {t('Edit')}
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Alerte lots périmés ── */}
        {product.expiredBatchCount > 0 && (
          <div className="product-expired-alert">
            <FontAwesomeIcon icon={faTriangleExclamation} className="product-expired-alert__icon" />
            <span>
              <strong>
                {t('product.expired_batches_alert', {
                  count: product.expiredBatchCount,
                  s: product.expiredBatchCount > 1 ? 's' : '',
                })}
              </strong>
            </span>
          </div>
        )}

        {/* ── Prix + Stock ── */}
        <div className="product-price-grid">
          <div className="product-price-card">
            <span className="product-price-card__icon" style={BRAND_ICON_STYLE}>
              <FontAwesomeIcon icon={faCoins} />
            </span>
            <div>
              <div className="product-price-label">{t('product.public_price')}</div>
              <div
                className={`product-price-value ${onEditPublicPrice ? 'product-price-value--editable' : ''}`}
                style={{ color: '#000', fontWeight: 700 }}
                onClick={onEditPublicPrice}
                data-tooltip-id={onEditPublicPrice ? 'tooltip' : undefined}
                data-tooltip-content={onEditPublicPrice ? t('product.edit_ppv_price') : undefined}
              >
                <PriceWithUnit amount={product.publicPrice} />
              </div>
              <div className="product-price-sub">
                {pphGapPct != null ? t('product.pph_gap', { pct: signedPct(pphGapPct) }) : t('product.sale_price')}
              </div>
            </div>
          </div>
          <div className="product-price-card">
            <span className="product-price-card__icon" style={BRAND_ICON_STYLE}>
              <FontAwesomeIcon icon={faHospital} />
            </span>
            <div>
              <div className="product-price-label">{t('product.hospital_price')}</div>
              {product.hospitalPrice
                ? (
                  <div
                    className={`product-price-value ${onEditHospitalPrice ? 'product-price-value--editable' : ''}`}
                    onClick={onEditHospitalPrice}
                    data-tooltip-id={onEditHospitalPrice ? 'tooltip' : undefined}
                    data-tooltip-content={onEditHospitalPrice ? t('product.edit_pph_price') : undefined}
                  >
                    <PriceWithUnit amount={product.hospitalPrice} />
                  </div>
                )
                : (
                  <div
                    className={`product-price-value product-price-value--muted ${onEditHospitalPrice ? 'product-price-value--editable' : ''}`}
                    onClick={onEditHospitalPrice}
                    data-tooltip-id={onEditHospitalPrice ? 'tooltip' : undefined}
                    data-tooltip-content={onEditHospitalPrice ? t('product.edit_pph_price') : undefined}
                  >
                    {t('product.not_defined')}
                  </div>
                )
              }
              <div className="product-price-sub">
                {pphGapPct != null ? t('product.vs_public_price', { pct: signedPct(pphGapPct) }) : '—'}
              </div>
            </div>
          </div>
          <div className="product-price-card">
            <span className="product-price-card__icon" style={STOCK_ICON_STYLES[stockLevel]}>
              <FontAwesomeIcon icon={STOCK_ICONS[stockLevel]} />
            </span>
            <div>
              <div className="product-price-label">{t('product.available_stock')}</div>
              <div
                className={`product-price-value product-price-value--${{ ok: 'stock', low: 'warning', out: 'danger' }[stockLevel]} ${onStockClick ? 'product-price-value--editable' : ''}`}
                onClick={onStockClick}
                data-tooltip-id={onStockClick ? 'tooltip' : undefined}
                data-tooltip-content={onStockClick ? t('product.view_stock') : undefined}
              >
                {product.totalStock ?? 0} <span className="product-price-unit">{(product.totalStock ?? 0) > 1 ? t('product.units') : t('product.unit')}</span>
              </div>
              <div className={`product-stock-gauge product-stock-gauge--${stockLevel}`}>
                <span style={{ width: `${stockGaugePct}%` }} />
              </div>
              <div className="product-price-sub">
                {{ ok: t('product.stock_ok'), low: t('product.low_stock'), out: t('product.out_of_stock') }[stockLevel]}
              </div>
            </div>
          </div>
          <div className="product-price-card">
            <span className="product-price-card__icon" style={BRAND_ICON_STYLE}>
              <FontAwesomeIcon icon={faBell} />
            </span>
            <div>
              <div className="product-price-label">{t('product.stock_alert_threshold')}</div>
              <div
                className={`product-price-value product-price-value--warning ${onEditMinThreshold ? 'product-price-value--editable' : ''}`}
                onClick={onEditMinThreshold}
                data-tooltip-id={onEditMinThreshold ? 'tooltip' : undefined}
                data-tooltip-content={onEditMinThreshold ? t('product.stock_alert_threshold') : undefined}
              >
                {effectiveStockAlertThreshold} <span className="product-price-unit">{effectiveStockAlertThreshold > 1 ? t('product.units') : t('product.unit')}</span>
              </div>
              <div className="product-price-sub">
                {stockVsThreshold > 0 ? t('product.above_threshold', { count: stockVsThreshold }) : t('product.reorder_needed')}
              </div>
            </div>
          </div>
          {/* Next expiration among the batches in stock (backend: nearestExpiration) — red when
              it's within EXPIRY_SOON_DAYS, so the pharmacist sells or returns those first */}
          <div className="product-price-card">
            <span className="product-price-card__icon" style={BRAND_ICON_STYLE}>
              <FontAwesomeIcon icon={faCalendarXmark} />
            </span>
            <div>
              <div className="product-price-label">{t('product.next_expiration')}</div>
              {nearestExpiration ? (
                <>
                  <div className={`product-price-value ${daysToExpiry <= EXPIRY_SOON_DAYS ? 'product-price-value--danger' : ''}`}>
                    {nearestExpiration.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                  <div className="product-price-sub">{t('product.expires_in_days', { count: daysToExpiry })}</div>
                </>
              ) : (
                <>
                  <div className="product-price-value product-price-value--muted">—</div>
                  <div className="product-price-sub">{t('product.no_batch_in_stock')}</div>
                </>
              )}
            </div>
          </div>
          {product.maximumStock > 0 && (
            <div className="product-price-card">
              <span className="product-price-card__icon" style={BRAND_ICON_STYLE}>
                <FontAwesomeIcon icon={faBoxesStacked} />
              </span>
              <div>
                <div className="product-price-label">{t('product.stock_max_threshold')}</div>
                <div
                  className={`product-price-value product-price-value--warning ${onEditMaxThreshold ? 'product-price-value--editable' : ''}`}
                  onClick={onEditMaxThreshold}
                  data-tooltip-id={onEditMaxThreshold ? 'tooltip' : undefined}
                  data-tooltip-content={onEditMaxThreshold ? t('product.stock_max_threshold') : undefined}
                >
                  {product.maximumStock} <span className="product-price-unit">{product.maximumStock > 1 ? t('product.units') : t('product.unit')}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      )}

      {/* ── 2 colonnes ── */}
      {part !== 'header' && (
      <div className="product-col-layout">

        {/* Colonne gauche */}
        <div>
          <SectionCard title={t('product.identification')} icon={faIdCard} iconStyle={BRAND_ICON_STYLE}>
            {/* Brand + strength (before the comma) in bold, the pharmaceutical form after it in regular weight */}
            <FieldRow label={t('product.denomination')}>
              {(() => {
                const comma = displayName?.indexOf(',') ?? -1;
                if (!displayName || comma < 0) return displayName ? <strong>{displayName}</strong> : '—';
                return <><strong>{displayName.slice(0, comma)}</strong>{displayName.slice(comma)}</>;
              })()}
            </FieldRow>
            <FieldRow label={t('product.presentation')} value={arText(product.presentationAr, product.presentation)} />
            <FieldRow label={t('product.dosage')} value={arText(product.dosageAr, product.dosage)} strong />
            <FieldRow label={t('product.family')} value={arText(product.familyAr, product.family)} />
            <FieldRow label={t('Laboratory')}>
              {product.laboratory?.id
                ? (
                  <NavLink
                    to={`/suppliers/${product.laboratory.id}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}
                  >
                    <LaboratoryLogo id={product.laboratory.id} name={product.laboratory.name} size={20} />
                    {product.laboratory.name}
                  </NavLink>
                )
                : '—'}
            </FieldRow>
            <FieldRow label={t('product.public_price')}>
              {product.publicPrice != null ? <strong><PriceWithUnit amount={product.publicPrice} /></strong> : '—'}
            </FieldRow>
            <FieldRow label={t('product.product_type')} value={product.productType} />
            <FieldRow label={t('Status')} value={product.status ? getStatusLabel(product.status, t) : null} />
            <FieldRow label={t('Refundable')}>
              {product.refundable === true ? t('Yes') : product.refundable === false ? t('No') : '—'}
            </FieldRow>
            <div className="product-field-row">
              <span className="product-field-label">{t('Barcode')}</span>
              <span className="product-field-value">
                <BarcodeComponent value={product.barcode} format="EAN13" height={40} fontSize={13} />
              </span>
            </div>
          </SectionCard>
        </div>

        {/* Colonne droite */}
        <div>
          <SectionCard title={t('product.clinical_info')} icon={faNotesMedical} iconStyle={BRAND_ICON_STYLE}>
            <FieldRow label={t('product.composition')} value={arText(product.compositionAr, product.composition)} truncate strong />
            <FieldRow label={t('product.indication')} value={arText(product.indicationAr, product.indication)} truncate />
            <FieldRow label={t('product.posology')} value={arText(product.dosagesAdministrationAr, product.dosagesAdministration)} truncate />
            <FieldRow
              label={t('product.contraindications')}
              value={arText(product.contraindicationAr, product.contraindication)}
              danger={!!product.contraindication && product.contraindication !== '--'}
              truncate
            />
            <FieldRow label={t('product.warnings')} value={arText(product.warningsAr, product.warnings)} truncate />
            <FieldRow label={t('product.atc_code')}>
              {product.atcCode ? (
                <>
                  {product.atcCode}
                  <FontAwesomeIcon
                    icon={faCircleInfo}
                    id="atc-code-info"
                    style={{ color: '#9ca3af', fontSize: 13, cursor: 'pointer', marginLeft: 8 }}
                  />
                  <Tooltip
                    anchorSelect="#atc-code-info"
                    place="top"
                    style={{ maxWidth: 280, fontSize: 12, lineHeight: 1.5, textAlign: 'left' }}
                  >
                    {t('product.atc_tooltip')}
                  </Tooltip>
                </>
              ) : '—'}
            </FieldRow>
            <FieldRow label={t('product.table')} value={product.tableLetter} />
            <FieldRow label={t('product.dependence')} value={product.dependanceRisk} truncate />
            <FieldRow label={t('product.psychoactive_substance')} value={product.psychoactiveSubstance} truncate />
          </SectionCard>

          {product?.images?.length > 0 && (
            <div className="product-images-strip">
              {product.images.map((img, i) => (
                <img key={i} src={img.url} alt={`${product.name} ${i + 1}`} className="product-images-strip__img" />
              ))}
            </div>
          )}
        </div>

      </div>
      )}
    </div>
  );
};

export default ProductInformation;
